<template>
    <div class="common-filter-tool">
        <div class="filters">
            <div
                class="clear-preview"
                v-if="!isShowingOnlyTemplate() && showClearAll && !hideFilters"
                @click="clearAll"
            >
                <i class="material-icons">clear</i>
                {{ $t("chatWindow.clearAll") }}
            </div>

            <CommonFilterItem
                v-for="(filter, key) in clone"
                :key="filter.type"
                v-model="clone[key]"
                v-bind="filter"
                :on-removed="() => onFilterRemoved(filter.name)"
                :default-open="lastAddedFilter === filter.type"
                :on-finished="() => (lastAddedFilter = null)"
                :autoRemoveWhenCancel="lastAddedFilter === filter.type"
                :label-formatter="labelFormatter"
                :removeable="filter.clearable === true || filter.clearable === undefined"
                v-if="!isShowingOnlyTemplate() && (!hideFilters || requiredFilters.includes(filter.name))"
            />

            <a-popover
                :visible="filterMenuVisible"
                :overlayClassName="'filterMenu no-padding'"
                placement="bottom"
                @visibleChange="(isVisible) => (filterMenuVisible = isVisible)"
                trigger="click"
                v-if="!isShowingOnlyTemplate() && getAvailableFilters().length && showAddButton"
            >
                <div slot="content">
                    <div class="custom-list-wrapper filter-list-bg">
                        <div class="custom-list">
                            <div
                                class="custom-list-item"
                                v-for="filter in getAvailableFilters()"
                                @click="() => addFilter(filter.name)"
                            >
                                <span class="material-icons">{{ filter.materialIcon || "filter_alt" }}</span>
                                {{ filter.label }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="add-button">
                    <span class="material-icons"> add </span>
                    <div class="add-button-text">{{ $t("chatbotv2.addFilter") }}</div>
                </div>
            </a-popover>
            <div class="add-button star" v-if="showSaveButton" @click="saveFilter">
                <span class="material-icons"> star </span>
                <div class="add-button-text">{{ $t("message.saveFilter") }}</div>
            </div>
            <a-popover
                :visible="templateMenuVisible"
                :overlayClassName="'filterMenu no-padding'"
                placement="bottom"
                @visibleChange="(isVisible) => (templateMenuVisible = isVisible)"
                trigger="click"
                v-if="isShowingOnlyTemplate() && filterTemplates.length"
                key="filterTemplates"
            >
                <div slot="content">
                    <div class="custom-list-wrapper filter-list-bg">
                        <div class="custom-list">
                            <div
                                class="custom-list-item"
                                v-for="filter in filterTemplates"
                                @click="() => applyFilterTemplate(filter.name)"
                            >
                                <span class="material-icons">{{ filter.materialIcon || "filter_alt" }}</span>
                                {{ filter.label }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="add-button">
                    <span class="material-icons"> filter_list </span>
                    <div
                        class="add-button-text"
                        v-if="selectedFilterTemplate"
                        v-tippy="{ content: selectedFilterTemplate }"
                    >
                        <template v-if="!minimize">{{ $t("calendar.list") }}:</template>
                        {{
                            selectedFilterTemplate.length > 15
                                ? selectedFilterTemplate.substring(0, 15) + "..."
                                : selectedFilterTemplate
                        }}
                    </div>
                    <div class="add-button-text" v-else>{{ $t("chatWindow.selectFilter") }}</div>
                    <span class="material-icons"> arrow_drop_down </span>
                </div>
            </a-popover>
            <slot name="extra" />
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../utils/log2";
import _ from "lodash";
import { mapState } from "vuex";
import CommonFilterItem from "./CommonFilterItem.vue";

const log = log2("CommonFilterTool");
export default {
    name: "CommonFilterTool",
    components: {
        CommonFilterItem,
    },
    props: {
        value: {
            type: Array,
            default: () => ({}),
        },
        minimize: {
            type: Boolean,
            default: false,
        },
        keepStateOnQueryParams: {
            type: Boolean,
            default: () => false,
        },
        filterMeta: {
            type: Array,
            required: true,
        },
        labelFormatter: {
            type: Function,
        },
        showClearAll: {
            type: Boolean,
            default: false,
        },
        filterTemplates: {
            type: Array,
            default: () => [],
        },
        selectedFilterTemplate: {
            type: String,
            default: "",
        },
        hideFilters: {
            type: Boolean,
            default: false,
        },
        showAddButton: {
            type: Boolean,
            default: true,
        },
        requiredFilters: {
            type: Array,
            default: () => [],
        },
        showOnlyTemplates: {
            type: Boolean,
            default: null,
        },
        showSaveButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            filterMenuVisible: false,
            templateMenuVisible: false,
            clone: _.cloneDeep(this.value),
            lastAddedFilter: null,
        };
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
        }),
    },
    methods: {
        saveFilter() {
            this.$emit("saveFilter");
        },
        isShowingOnlyTemplate() {
            return this.showOnlyTemplates !== null && this.showOnlyTemplates === true;
        },
        clearAll() {
            this.clone = this.clone.filter((x) => x.clearable === false);
            this.$emit("input", this.clone);
            this.$emit("clearAll");
        },
        getAvailableFilters() {
            return this.filterMeta.filter((filter) => {
                return !this.clone.map((x) => x.name).includes(filter.name);
            });
        },
        addFilter(name) {
            log("addFilter", name);
            this.$emit("addFilter", name);
            this.lastAddedFilter = name;
            this.filterMenuVisible = false;
        },
        applyFilterTemplate(name) {
            log("applyFilterTemplate", name);
            this.templateMenuVisible = false;
            this.$emit("applyFilterTemplate", name);
        },
        onFilterRemoved(name) {
            log("onFilterRemoved", name);
            if (this.clone.clearable === false) {
                return;
            }
            this.clone = this.clone.filter((x) => x.name !== name);
            this.$emit("removeFilter", name);
        },

        updateQueryParams(key, value) {
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                this.$router.replace({
                    query: { ...query, [key]: encodeURI(value) },
                });
            }
        },
        keepState(filters) {
            if (this.keepStateOnQueryParams) {
                // this.updateQueryParams("period", filters["period"]);
            }
        },
    },
    mounted() {
        log("this.value...", this.value);
    },
    watch: {
        clone: {
            handler(newValue) {
                log("filter changed", newValue);
                this.$emit("input", newValue);
                this.keepState(newValue);
            },
            deep: true,
        },
        lastAddedFilter(newVal) {
            if (!newVal) {
                if (!_.isEqual(this.clone, this.value)) {
                    this.$emit("input", this.clone);
                }
            }
        },
        value: {
            handler(newValue) {
                if (_.isEqual(newValue, this.clone)) {
                    return;
                }
                this.clone = _.cloneDeep(newValue);
                this.lastAddedFilter = null;
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.common-filter-tool {
    border-bottom: 1px solid #dbe1e8;
    overflow: hidden;
    padding: 20px 20px 10px;

    .clear-preview {
        align-items: center;
        background: #22272d;
        color: #fff;
        cursor: pointer;
        display: flex;
        font-size: 16px;
        height: 32px;
        transition: all 0.3s;
        white-space: nowrap;
        width: max-content;
        border-radius: 20px;
        padding: 1px 5px !important;
    }

    .filters {
        display: flex;
        flex-wrap: wrap;
        overflow-x: hidden;
        overflow-y: auto;
        width: 100%;
        gap: 4px;
    }

    .add-button {
        margin-left: 10px;
        display: flex;
        min-width: 80px;
        align-items: center;
        width: fit-content !important;
        height: 32px;
        margin: 0 10px 10px 0;
        cursor: pointer;
        transition: all 0.3s;
        background: #fff;
        border: 1px solid #dbe1e8;
        color: #22272d;
        line-height: 28px;
        border-radius: 4px;
        font-family: Source Sans Pro, sans-serif;
        font-weight: 600;
        padding: 0 10px;
        text-align: center;
        user-select: none;

        .material-icons {
            color: #006cff;
        }

        &:hover {
            opacity: 0.8;
        }
    }
}

.custom-list-wrapper {
    display: flex;
    flex-direction: column;
    max-height: 99vh;
    max-width: 300px;
    overflow: hidden;

    .custom-list {
        overflow-x: hidden;
        overflow-y: auto;

        .custom-list-item {
            align-items: center;
            cursor: pointer;
            display: flex;
            height: 35px;
            padding: 0 16px;
            transition: all 0.3s;
            width: 100%;
            background: #f5f7fa;
            overflow: hidden !important;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: #22272d;
            font-weight: 400;
            font-size: 15px;
            line-height: 19px;

            .material-icons {
                color: #008aff;
                font-size: 14px;
                margin-right: 10px;
                transition: all 0.3s;
            }

            &:hover {
                background: #008aff;
                color: #fff;
                opacity: 0.8;

                .material-icons {
                    color: #fff;
                }
            }
        }

        .custom-list-item:first-child {
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
        }
    }
}

.ant-popover.no-padding {
    .ant-popover-inner-content {
        padding: 0 !important;
        width: auto;
    }
}
</style>
